import { Frown } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import { RESOLUTION_STATUS_COLORS } from '@/Domain/hooks/Analytics/resolutionStatusColors';
import { toKey } from '@/Domain/hooks/Analytics/useConversationAnalytics.hook';

type Props = {
  totals: Record<string, number>;
  labels: readonly string[];
  isLoading: boolean;
};

const RADIAL_GAP_RATIO = 0.006;

export const ResolutionBreakdownCard = ({ totals, labels, isLoading }: Props) => {
  const { t } = useTranslation('dashboard', { keyPrefix: 'conversations' });

  const items = useMemo(
    () =>
      labels.map((label) => {
        const key = toKey(label);
        return {
          key,
          title: label,
          count: totals[key] ?? 0,
          color: RESOLUTION_STATUS_COLORS[key] ?? 'hsl(220 9% 46%)',
        };
      }),
    [labels, totals],
  );

  const total = useMemo(() => items.reduce((sum, item) => sum + item.count, 0), [items]);
  const hasData = total > 0;

  const totalResolved = (totals['confirmed_resolution'] ?? 0) + (totals['assumed_resolution'] ?? 0);
  const resolutionRate = total > 0 ? (totalResolved / total) * 100 : 0;

  const visibleItems = useMemo(() => items.filter((item) => item.count > 0), [items]);

  // RadialBarChart needs a single data row with each status as a separate key.
  // Gap entries (_gap0, _gap1, …) create visual spacing between segments.
  const chartData = useMemo(() => {
    const gapSize = total * RADIAL_GAP_RATIO;
    const data: Record<string, number> = {};
    visibleItems.forEach((item, i) => {
      data[item.key] = item.count;
      if (i < visibleItems.length - 1) {
        data[`_gap${i}`] = gapSize;
      }
    });
    return [data];
  }, [visibleItems, total]);

  const chartConfig = useMemo(
    () =>
      Object.fromEntries(items.map((item) => [item.key, { label: item.title, color: item.color }])),
    [items],
  );

  return (
    <Card className={`pt-0 ${isLoading ? 'animate-pulse' : ''}`}>
      <CardHeader>
        <CardTitle>{t('resolution_breakdown', 'Resolution Breakdown')}</CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <ChartPlaceholder
            icon={Frown}
            titleTranslationKey="no_data_in_range.title"
            descriptionTranslationKey="no_data_in_range.description"
            spacing="small"
          />
        ) : (
          <ChartContainer config={chartConfig} className="mx-auto w-full h-52">
            <RadialBarChart data={chartData} endAngle={180} innerRadius={80} outerRadius={130}>
              <ChartTooltip
                cursor={false}
                content={(props) => (
                  <ChartTooltipContent
                    {...props}
                    hideLabel
                    payload={props.payload?.filter((p) => !String(p.dataKey).startsWith('_gap'))}
                  />
                )}
              />
              <PolarRadiusAxis axisLine={false} tick={false} tickLine={false}>
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text textAnchor="middle" x={viewBox.cx} y={viewBox.cy}>
                          <tspan
                            className="fill-foreground text-2xl font-medium"
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) - 16}
                          >
                            {Math.round(resolutionRate)}%
                          </tspan>
                          <tspan
                            className="fill-muted-foreground"
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 4}
                          >
                            {totalResolved.toLocaleString()} {t('conversations_resolved')}
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </PolarRadiusAxis>
              {visibleItems.flatMap((item, i) => [
                <RadialBar
                  key={item.key}
                  className="stroke-transparent stroke-2"
                  cornerRadius={5}
                  dataKey={item.key}
                  fill={`var(--color-${item.key})`}
                  stackId="a"
                />,
                ...(i < visibleItems.length - 1
                  ? [
                      <RadialBar
                        key={`_gap${i}`}
                        cornerRadius={0}
                        dataKey={`_gap${i}`}
                        fill="var(--card)"
                        stackId="a"
                      />,
                    ]
                  : []),
              ])}
            </RadialBarChart>
          </ChartContainer>
        )}
      </CardContent>
      {hasData && (
        <CardFooter className="-mt-14">
          <div className="grid w-full grid-cols-2 gap-x-4 gap-y-3">
            {[...items].reverse().map((item) => {
              const pct = ((item.count / total) * 100).toFixed(1);
              return (
                <div key={item.key} className="flex flex-col">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="size-2 rounded-full shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-muted-foreground text-xs">{item.title}</span>
                  </div>
                  <span className="text-sm font-medium tabular-nums pl-3.5">
                    {item.count.toLocaleString()} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};
